import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { getMessaging, isSupported as isMessagingSupported, type Messaging } from 'firebase/messaging';

// authDomain drives the OAuth redirect_uri (`https://<authDomain>/__/auth/handler`).
// It MUST be a domain that is both an authorized domain in Firebase Auth AND an
// authorized redirect URI on the Google OAuth client — otherwise Google answers
// `Error 400: redirect_uri_mismatch` and nobody can sign in with Google.
//
// Never derive it from window.location.hostname: every host the app is served
// from (apex, www, preview channels) would need its own redirect URI registered.
// Default to the project's firebaseapp.com domain, which Firebase registers
// automatically. To brand the consent screen with a custom domain, set
// VITE_FIREBASE_AUTH_DOMAIN to it *after* adding
// `https://<that-domain>/__/auth/handler` to the OAuth client in Google Cloud
// Console → APIs & Services → Credentials.
const authDomain =
  import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
  `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);

// Initialize Analytics (only in browser and if supported)
export const initAnalytics = async () => {
  if (await isSupported()) {
    return getAnalytics(app);
  }
  return null;
};

// Cloud Messaging (web push). Returns null where the browser can't support it
// (e.g. iOS Safari without a home-screen PWA, or no service worker).
export const getMessagingIfSupported = async (): Promise<Messaging | null> => {
  try {
    if (!(await isMessagingSupported())) return null;
    return getMessaging(app);
  } catch {
    return null;
  }
};

// Connect to emulators in development
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  console.log('🔧 Connecting to Firebase Emulators...');

  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectStorageEmulator(storage, 'localhost', 9199);

  console.log('✅ Connected to Firebase Emulators');
}
